import { Box, Button, Chip, Divider, Link, Paper, Stack, Typography } from '@mui/material'
import { SectionLoader } from 'Components/Section'
import { SchoolPath } from 'Constants/Navigation'
import { UserBasic } from 'Entities/account'
import { useStore } from 'effector-react'
import { $Account } from 'Models'
import { useNavigate, useParams } from 'react-router-dom'
import { useSchoolAnnouncementDetail } from '../Hooks/useSchoolAnnouncements'
import { SchoolAnnouncement } from '../types'

const formatDate = (value?: string | null) => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const getAnnouncementTitle = (item: SchoolAnnouncement) => item.titleRu || item.titleUz || item.titleKo || 'Объявление'

const getSchoolTitle = (item: SchoolAnnouncement) =>
  item.school?.schoolNameRu ||
  item.schoolNameRu ||
  item.school?.schoolNameUz ||
  item.schoolNameUz ||
  item.school?.schoolNameKo ||
  item.schoolNameKo ||
  'Неизвестная школа'

const getSchoolRegion = (item: SchoolAnnouncement) => item.school?.regionName || item.regionName

const hasSchool = (item: SchoolAnnouncement) =>
  !!(
    item.school?.schoolId ||
    item.schoolId ||
    item.school?.schoolNameRu ||
    item.schoolNameRu ||
    item.school?.schoolNameUz ||
    item.schoolNameUz ||
    item.school?.schoolNameKo ||
    item.schoolNameKo
  )

const getAccountName = (account: UserBasic | null) => [account?.firstName, account?.lastName].filter(Boolean).join(' ').trim()

const getCreatorName = (item: SchoolAnnouncement, account: UserBasic | null) => {
  const accountName = getAccountName(account)
  if (accountName) return accountName

  const fullName = [item.firstName || item.createdByFirstName, item.lastName || item.createdByLastName].filter(Boolean).join(' ').trim()

  return fullName || item.createdBy || '-'
}

const getAnnouncementOwnerTitle = (item: SchoolAnnouncement, account: UserBasic | null) => {
  if (!hasSchool(item)) return `Superadmin (${getCreatorName(item, account)})`

  return getSchoolTitle(item)
}

const renderContentBlock = (title: string, value: string) => {
  if (!value) return null

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant='subtitle2' color='text.secondary' mb={1.5}>
        {title}
      </Typography>
      <Box
        sx={{
          color: 'text.primary',
          fontSize: 15,
          lineHeight: 1.7,
          overflowWrap: 'anywhere',
          '& p': { mt: 0, mb: 1.5 },
          '& img': { maxWidth: '100%', height: 'auto' },
          '& table': { maxWidth: '100%', borderCollapse: 'collapse' },
          '& td, & th': { border: '1px solid', borderColor: 'divider', p: 1 },
        }}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    </Paper>
  )
}

export const SchoolAnnouncementDetailPage = () => {
  const { announcementId } = useParams<{ announcementId: string }>()
  const navigate = useNavigate()
  const account = useStore($Account)
  const { announcement, isLoading, isFetching } = useSchoolAnnouncementDetail(announcementId)

  const handleBack = () => {
    navigate(`/${SchoolPath.main}/${SchoolPath.announcements}`)
  }

  return (
    <SectionLoader isLoading={isLoading} isFetching={isFetching}>
      {announcement ? (
        <Stack spacing={3}>
          <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
            <Box>
              <Button className='light' onClick={handleBack} sx={{ mb: 1.5 }}>
                Назад к списку
              </Button>
              <Typography variant='h4'>{getAnnouncementTitle(announcement)}</Typography>
              <Stack direction='row' spacing={1} flexWrap='wrap' mt={1.5}>
                <Chip label={getAnnouncementOwnerTitle(announcement, account)} variant='outlined' />
                {getSchoolRegion(announcement) ? <Chip label={getSchoolRegion(announcement)} variant='outlined' /> : null}
              </Stack>
            </Box>
          </Stack>

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Stack divider={<Divider flexItem />} spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box flex={1}>
                  <Typography variant='body2' color='text.secondary'>
                    Опубликовано
                  </Typography>
                  <Typography variant='subtitle2' mt={0.5}>
                    {formatDate(announcement.publishedDate)}
                  </Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant='body2' color='text.secondary'>
                    Последнее изменение
                  </Typography>
                  <Typography variant='subtitle2' mt={0.5}>
                    {formatDate(announcement.lastModifiedDate || announcement.createdDate)}
                  </Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant='body2' color='text.secondary'>
                    Автор
                  </Typography>
                  <Typography variant='subtitle2' mt={0.5}>
                    {getCreatorName(announcement, account)}
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <Typography variant='body2' color='text.secondary' mb={1}>
                  Вложения
                </Typography>
                {announcement.attachments?.length ? (
                  <Stack direction='row' spacing={1} flexWrap='wrap'>
                    {announcement.attachments.map(attachment => (
                      <Chip
                        key={attachment.id}
                        label={attachment.originalFileName}
                        component={Link}
                        href={attachment.path}
                        target='_blank'
                        clickable
                        variant='outlined'
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant='subtitle2'>-</Typography>
                )}
              </Box>
            </Stack>
          </Paper>

          <Stack spacing={2}>
            {renderContentBlock('Русский', announcement.contentRu)}
            {renderContentBlock('Узбекский', announcement.contentUz)}
            {renderContentBlock('Корейский', announcement.contentKo)}
          </Stack>
        </Stack>
      ) : (
        !isLoading && (
          <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant='h6'>Объявление не найдено</Typography>
            <Button className='light' onClick={handleBack} sx={{ mt: 2 }}>
              Назад к списку
            </Button>
          </Paper>
        )
      )}
    </SectionLoader>
  )
}
